import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useExam } from '../hooks/useExam';
import { Badge, Button, Card, LoadingState } from '../components/ui';
import ExamStatusCard from '../components/ExamStatusCard';
import AdBanner from '../components/AdBanner';
import './DashboardPage.css';
import { formatDate, formatMinutes } from '../utils/format';

interface DashboardPageProps {
  onStartExam?: (sessionId: string) => void;
  onViewResults?: (sessionId: string) => void;
  onGoToExamSelection?: () => void;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ onStartExam, onGoToExamSelection }) => {
  const { user } = useAuth();
  const { examTypes, currentSession, examHistory, isLoading, error } = useExam();
  const [notices, setNotices] = useState<any[]>([]);
  const [selectedNotice, setSelectedNotice] = useState<any>(null);

  const showAds = !user?.paid_plan || user.paid_plan === 0;

  const metrics = useMemo(() => {
    const total = examHistory.length;
    const avg = total > 0 ? `${Math.round(examHistory.reduce((sum, exam) => sum + exam.percentage, 0) / total)}%` : '-';
    const best = total > 0 ? `${Math.max(...examHistory.map((exam) => exam.percentage))}%` : '-';
    const avgTime = total > 0
      ? formatMinutes(examHistory.reduce((sum, exam) => sum + (exam.timeTakenMinutes || 0), 0) / total)
      : '-';

    return [
      { id: 'available', label: '응시 가능 시험', value: String(examTypes.length) },
      { id: 'total', label: '완료한 시험', value: String(total) },
      { id: 'avg', label: '평균 점수', value: avg },
      { id: 'best', label: '최고 점수', value: best },
      { id: 'time', label: '평균 소요 시간', value: avgTime }
    ];
  }, [examHistory, examTypes.length]);

  const loadNotices = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/board/posts?category=notice&pinned_only=true&limit=5`);
      if (!response.ok) return;

      const result = await response.json();
      if (result.success) {
        setNotices(result.data.posts || []);
      }
    } catch {
      // Dashboard notices are optional context.
    }
  };

  useEffect(() => {
    loadNotices();
  }, []);

  const handleStartExam = () => {
    onGoToExamSelection?.();
  };

  const handleContinueExam = (sessionId: string) => {
    onStartExam?.(sessionId);
  };

  if (isLoading) {
    return <LoadingState label="대시보드 데이터를 불러오는 중입니다..." />;
  }

  return (
    <div className="dashboard-v2">
      {error && (
        <div className="dashboard-v2__error" role="alert">{error}</div>
      )}

      <section className="dashboard-v2__hero">
        <span className="dashboard-v2__hero-eyebrow">학습 대시보드</span>
        <h1>{user?.name ? `${user.name}님, ` : ''}다음 AWS 모의시험을 준비해볼까요?</h1>
        <p>응시 가능한 시험, 진행 중인 세션, 최근 결과를 한 화면에서 확인하세요.</p>
        <div className="dashboard-v2__hero-actions">
          {currentSession ? (
            <Button variant="secondary" size="lg" onClick={handleStartExam}>
              다른 시험 선택
            </Button>
          ) : (
            <Button variant="primary" size="lg" onClick={handleStartExam} disabled={examTypes.length === 0}>
              모의시험 시작하기
            </Button>
          )}
        </div>
      </section>

      <section className="dashboard-v2__status-wrap">
        <ExamStatusCard
          examTypes={examTypes}
          currentSession={currentSession}
          examHistory={examHistory}
          onStartExam={handleStartExam}
          onContinueExam={handleContinueExam}
        />
      </section>

      <section className="dashboard-v2__metrics-grid" aria-label="학습 지표">
        {metrics.map((metric) => (
          <Card key={metric.id} padding="md" className="dashboard-v2__metric">
            <span className="dashboard-v2__metric-label">{metric.label}</span>
            <strong className="dashboard-v2__metric-value">{metric.value}</strong>
          </Card>
        ))}
      </section>

      <Card padding="lg" tone="major" className="dashboard-v2__notices-card">
        <div className="dashboard-v2__section-head">
          <h3>공지사항</h3>
          <span>{notices.length}건</span>
        </div>

        {notices.length === 0 ? (
          <div className="dashboard-v2__empty">고정된 공지사항이 없습니다.</div>
        ) : (
          <ul className="dashboard-v2__notice-list">
            {notices.map((notice) => (
              <li key={notice.post_id}>
                <button
                  className="dashboard-v2__notice-btn"
                  onClick={() => setSelectedNotice(notice)}
                >
                  <span className="dashboard-v2__notice-title">
                    {notice.is_pinned && <Badge tone="info">고정</Badge>}
                    {notice.title}
                  </span>
                  <span className="dashboard-v2__notice-date tabular">{formatDate(notice.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <AdBanner showAds={showAds} className="dashboard-v2__ad" />

      {selectedNotice && (
        <div className="dashboard-v2__modal" onClick={() => setSelectedNotice(null)}>
          <div className="dashboard-v2__modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-v2__modal-head">
              <h3>{selectedNotice.title}</h3>
              <button onClick={() => setSelectedNotice(null)} aria-label="공지 닫기">닫기</button>
            </div>
            <div className="dashboard-v2__modal-meta">
              <span>작성일 {formatDate(selectedNotice.created_at)}</span>
              <span>작성자 {selectedNotice.author_name}</span>
            </div>
            <div className="dashboard-v2__modal-body">
              {selectedNotice.content.split('\n').map((line: string, idx: number) => (
                <p key={idx}>{line}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
